import React, { useContext, useEffect, useRef, useState } from "react";

import FrontPageLogo from "../Logo/Logo";
import { GiEntryDoor } from "react-icons/gi";
import { Redirect, useHistory } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import i18n, { t } from "i18next";
import QuestionHeader from "../AlternateQuestionHeader/AlternateQuestionHeader";
import Switch from "@mui/material/Switch";
import Feedback from "../../shared/api/FeedbackApi";
import { default as MuiButton } from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CloseIcon from "@mui/icons-material/Close";
import {
  InputLabel,
  FormControl,
  MenuItem,
  Autocomplete,
  AutocompleteChangeReason,
  Backdrop,
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Dialog,
  Divider,
  Grid,
  Paper,
  Select as MuiSelect,
  Slider,
  Snackbar,
  Tab,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import _ from "underscore";
import CardDeck from "../CardDeck/CardDeck";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";

import PreviewCard from "../PreviewCard/PreviewCard";
import CardWarning from "../CardWarning/CardWarning";

import RefreshIcon from "@mui/icons-material/Refresh";
import {
  QuestionCardInfo,
  AnswerCardInfo,
  CardInfo,
  CardType,
  Language,
} from "../../shared/interfaces/GameLogicTypes";
import {
  convertDimensionsToPixels,
  getElementHeight,
  getRandomTsarName,
  getLanguageByLanguageId,
} from "../../shared/helper/functions";
import CardCreationWidget from "../CardCreationWidget/CardCreationWidget";
import { Languages } from "../../shared/language/languages";
import { GameContext } from "../../shared/GameContext";
import { useAuth0 } from "@auth0/auth0-react";
import Deck from "../../shared/api/DeckApi";

export interface CardPoolProps {
  previewDeckId: string;
  setClose: () => void;
  friendPool?: string[];
}

export default function CardDeckViewer(props: CardPoolProps) {
  const [tsarName, setTsarName] = useState(getRandomTsarName());
  const getFriendName = (index: number) =>
    props.friendPool && props.friendPool.length > 0
      ? props.friendPool[index % props.friendPool.length]
      : "{{friend}}";
  const [cardPoolTab, setCardPoolTab] = useState<string>("questions");
  const [cardPoolSubTab, setCardPoolSubTab] = useState<string>("for");
  const [previewCategory, setPreviewCategory] = useState("");
  const [questionCards, setQuestionCards] = useState<Array<QuestionCardInfo>>([]);
  const [answerCards, setAnswerCards] = useState<Array<AnswerCardInfo>>([]);
  const [deckDescription, setDeckDescription] = useState("");
  const [deckName, setDeckName] = useState("");
  const [deckCreatorName, setDeckCreatorName] = useState("");
  const [deckLanguage, setDeckLanguage] = useState<Language>(Languages.english as Language);
  const [deckFeatured, setDeckFeatured] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState("offensive");
  const [reportMessage, setReportMessage] = useState("");
  const [reportSnackbar, setReportSnackbar] = useState("");

  const handleCardPoolTabChange = (event: React.SyntheticEvent, newTabValue: string) => {
    setCardPoolTab(newTabValue);
  };

  const handleCardPoolSubTabChange = (event: React.SyntheticEvent, newTabValue: string) => {
    setCardPoolSubTab(newTabValue);
  };

  useEffect(() => {
    if (props.previewDeckId != "") {
      const fetchDeck = async () => {
        console.log("fetchin deck to deckId " + props.previewDeckId);
        let deck = await Deck.loadDeck(props.previewDeckId);
        setQuestionCards(deck.questionCards);
        setAnswerCards(deck.answerCards);
        setDeckDescription(
          deck.deckInfo.description ? deck.deckInfo.description : "No Description"
        );
        setDeckName(deck.deckInfo.name ? deck.deckInfo.name : "No Name");
        setDeckCreatorName(deck.deckInfo.creatorName);
        setDeckFeatured(deck.deckInfo.featured ?? false);
        let newLanguage = getLanguageByLanguageId(deck.deckInfo.language);
        if (newLanguage !== undefined) {
          setDeckLanguage(newLanguage);
        }
      };
      fetchDeck();
    }
  }, [props.previewDeckId]);

  // Close on ESC: if a category overlay is open, close it; otherwise close the viewer
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (previewCategory !== "") {
          setPreviewCategory("");
        } else {
          props.setClose();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [previewCategory, props.setClose]);

  return (
    <>
      <Stack
        direction={{ md: "row", xs: "column" }}
        sx={{
          width: { xs: "90%", md: "75%" },
          height: {md:"80%", xs: "auto"},
          maxHeight: { xs: "95vh", md: "80%" },
          overflow:"auto"
        }}
        spacing={2}
      >
        <Paper
          id="new"
          sx={{
            width: { xs: "calc(100% - 2em)", md: "calc(25% - 2em)" },
            padding: "1em",
            backgroundColor: "#fff", // prevent see-through of underlying icons
            flexShrink: 0,
            overflow: "auto",
          }}
        >
          <Stack direction="column" alignItems="center" spacing={2} sx={{ width: "100%" }}>
            <TextField
              id="outlined-name"
              label="Name"
              value={deckName}
              sx={{ width: "100%" }}
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              id="outlined-name"
              label="Language"
              value={deckLanguage.name}
              sx={{ width: "100%" }}
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              fullWidth
              value={deckDescription}
              InputProps={{
                readOnly: true,
              }}
              id="deckDescription"
              label={"Description"}
              multiline
              rows={4}
              sx={{ width: "100%" }}
            />
            <TextField
              id="outlined-name"
              label={t("deck_viewer_created_by")}
              value={deckCreatorName}
              sx={{ width: "100%" }}
              InputProps={{
                readOnly: true,
              }}
            />
            <Button fullWidth variant="contained" onClick={props.setClose}>
              {t("close")}
            </Button>
            {!deckFeatured && (
              <Button
                fullWidth
                variant="outlined"
                color="warning"
                startIcon={<FlagIcon />}
                onClick={() => setReportDialogOpen(true)}
              >
                {t("report")}
              </Button>
            )}
          </Stack>
        </Paper>
        <Paper
          id="new"
          sx={{
            width: { xs: "100%", md: "75%" },
            
            height: {md:"100%", xs:"max-content"},
            
            overflow: {md:"auto"},
            backgroundColor: "#fff", // prevent see-through of underlying icons
          }}
        >
          <Stack>
            <TabContext value={cardPoolTab}>
              <TabList
                id="tab-list"
                centered
                variant="fullWidth"
                onChange={handleCardPoolTabChange}
              >
                <Tab sx={{ textTransform: "none" }} label={t("deck_viewer_questions")} value="questions" />
                <Tab sx={{ textTransform: "none" }} label={t("deck_viewer_answers")} value="answers" />
              </TabList>
              <TabPanel sx={{ padding: "0 !important" }} value="questions">
                <TabContext value={cardPoolSubTab}>
                  <TabList
                    id="tab-list"
                    centered
                    variant="fullWidth"
                    onChange={handleCardPoolSubTabChange}
                    sx={{ width: "100% !important" }}
                  >
                    <Tab sx={{ textTransform: "none" }} label={t("deck_viewer_for_tsar")} value="for" />
                    <Tab sx={{ textTransform: "none" }} label={t("deck_viewer_about_tsar")} value="about" />
                  </TabList>
                  <TabPanel sx={{ overflow: { md: "scroll", xs: "auto" }, padding: 0 }} value="for">
                    {/* "calc(" + convertHeightToPixels(getElementHeight("card-deck-creator-rows")) + " - 2em)" */}
                    <Box
                      sx={{
                        overflowX: "hidden",
                        overflowY: "scroll",
                        height:"100%"
                        //   height: {
                        //       md: "calc( " +
                        //           cardDeckCreatorElementHeight +
                        //           " - " +
                        //           convertHeightToPixels(getElementHeight("tab-list")) +
                        //           " - " +
                        //           convertHeightToPixels(getElementHeight("tab-list")) +
                        //           " ) ",
                        //   },
                      }}
                    >
                      <Grid
                        alignItems={"center"}
                        container
                        wrap="wrap"
                        justifyContent="flex-start"
                        spacing={2}
                        columns={{ xs: 2, sm: 4, md: 3, lg: 4, xl: 5 }}
                        sx={{ paddingBlock: ".5em", paddingInline: { md: "2em", xs: "1em" } }}
                      >
                        {questionCards.map((questionCard, idx) => {
                          return (
                            <Grid item xs={1} key={"questionCard-no-" + questionCard.id}>
                              <Stack alignItems={"center"}>
                                <PreviewCard
                                  key={"questionCard-no" + questionCard.id}
                                  text={questionCard.forTsar}
                                  tsarName={tsarName}
                                  friendName={getFriendName(idx)}
                                  categories={questionCard.categories}
                                  previewCategory={setPreviewCategory}
                                  probability={questionCard.probability}
                                  picassoSuitable={questionCard.picassoSuitable}
                                />
                              </Stack>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </TabPanel>
                  <TabPanel sx={{ overflow: "scroll", padding: 0 }} value="about">
                    {/* "calc(" + convertHeightToPixels(getElementHeight("card-deck-creator-rows")) + " - 2em)" */}
                    <Box
                      sx={{
                        overflowX: "hidden",
                        overflowY: "scroll",
                        //   height: {
                        //       md: "calc( " +
                        //           cardDeckCreatorElementHeight +
                        //           " - " +
                        //           convertHeightToPixels(getElementHeight("tab-list")) +
                        //           " - " +
                        //           convertHeightToPixels(getElementHeight("tab-list")) +
                        //           " ) ",
                        //   },
                      }}
                    >
                      <Grid
                        alignItems={"center"}
                        container
                        wrap="wrap"
                        justifyContent="flex-start"
                        spacing={2}
                        columns={{ xs: 2, sm: 4, md: 3, lg: 4, xl: 5 }}
                        sx={{ paddingBlock: ".5em", paddingInline: { md: "2em", xs: "1em" } }}
                      >
                        {questionCards.map((questionCard, idx) => {
                          return (
                            <Grid item xs={1} key={"questionCard-no-" + questionCard.id}>
                              <Stack alignItems={"center"}>
                                <PreviewCard
                                  text={questionCard.aboutTsar}
                                  tsarName={tsarName}
                                  friendName={getFriendName(idx)}
                                  categories={questionCard.categories}
                                  previewCategory={setPreviewCategory}
                                  probability={questionCard.probability}
                                  picassoSuitable={questionCard.picassoSuitable}
                                />
                              </Stack>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </TabPanel>
                </TabContext>
              </TabPanel>

              <TabPanel value="answers" sx={{ overflow: { md: "scroll", xs: "auto" }, padding: 0 }}>
                <div style={{ height: "100%" }}>
                  <Box
                    sx={{
                      overflowX: "hidden",
                      overflowY: { md: "scroll", xs: "auto" },
                     
                      //   height: {
                      //       md: "calc( " +
                      //           cardDeckCreatorElementHeight +
                      //           " - " +
                      //           convertHeightToPixels(getElementHeight("tab-list")) +
                      //           " ) ",
                      //   },
                    }}
                  >
                    <Grid
                      alignItems={"center"}
                      container
                      
                      justifyContent="flex-start"
                      spacing={2}
                      columns={{ xs: 2, sm: 4, md: 3, lg: 4, xl: 5 }}
                      sx={{ paddingBlock: ".5em", paddingInline: { md: "2em", xs: "1em" } }}
                    >
                      {answerCards.map((answerCard, idx) => {
                        return (
                          <Grid item xs={1} key={"answerCard-no" + answerCard.id}>
                            <Stack alignItems={"center"}>
                              <PreviewCard
                                text={answerCard.answer}
                                tsarName={tsarName}
                                friendName={getFriendName(idx)}
                                categories={answerCard.categories}
                                previewCategory={setPreviewCategory}
                                probability={answerCard.probability}
                              />
                            </Stack>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                </div>
              </TabPanel>
            </TabContext>
          </Stack>
        </Paper>{" "}
      </Stack>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={previewCategory != ""}
      >
        <IconButton
          id="topRight"
          onClick={() => setPreviewCategory("")}
          sx={{
            position: "absolute",
            top: "0",
            right: "0",
            marginRight: { md: "3em", xs: "1em" },
            marginTop: { md: "3em", xs: "1em" },
          }}
        >
          <CloseIcon
            color={"secondary"}
            sx={{ height: { md: "2.8em", xs: "1em" }, width: { md: "2.8em", xs: "1em" } }}
          />
        </IconButton>
        <Stack alignItems={"center"} justifyContent="flex-end" sx={{ height: "100vh" }} spacing={4}>
          <Typography variant="h2">{previewCategory} </Typography>
          <Stack
            direction={{ xs: "column", md: "row" }}
            sx={{ height: "75vh", width: { md: "100vw", xs: "100vw" } }}
            justifyContent="center"
            // alignItems={{xs:"center"}}
            spacing={{ md: 8, xs: 2 }}
          >
            <Stack
              direction={{ xs: "row", md: "column" }}
              sx={{
                overflowY: { xs: "auto", md: "scroll" },
                overflowX: { xs: "scroll", md: "auto" },
                paddingInline: "1em",
                paddingBlock: { xs: "1em", md: 0 },
                minWidth: { md: "16em", xs: "" },
              }}
              alignItems="center"
              spacing={2}
            >
              {questionCards
                .filter((card) => {
                  return card.categories.includes(previewCategory);
                })
                .map((card, idx) => {
                  return (
                    <PreviewCard
                      bigPreview
                      text={cardPoolSubTab == "for" ? card.forTsar : card.aboutTsar}
                      tsarName={tsarName}
                      friendName={getFriendName(idx)}
                    />
                  );
                })}
            </Stack>
            <Divider />
            <Stack
              direction={{ xs: "row", md: "column" }}
              sx={{
                overflowY: { xs: "auto", md: "scroll" },
                overflowX: { xs: "scroll", md: "auto" },
                paddingInline: "1em",
                paddingBlock: { xs: "1em", md: 0 },
                minWidth: { md: "16em", xs: "" },
              }}
              alignItems="center"
              spacing={2}
            >
              {answerCards
                .filter((card) => {
                  return card.categories.includes(previewCategory);
                })
                .map((card, idx) => {
                  return <PreviewCard bigPreview text={card.answer} tsarName={tsarName} friendName={getFriendName(idx)} />;
                })}
            </Stack>
          </Stack>
        </Stack>
      </Backdrop>
      <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)}>
        <DialogTitle>{t("deck_viewer_report_deck")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("deck_viewer_report_why")}
          </DialogContentText>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t("deck_viewer_report_reason")}</InputLabel>
            <MuiSelect
              value={reportReason}
              label={t("deck_viewer_report_reason")}
              onChange={(e) => setReportReason(e.target.value)}
            >
              <MenuItem value="offensive">{t("deck_viewer_report_offensive")}</MenuItem>
              <MenuItem value="inappropriate">{t("deck_viewer_report_inappropriate")}</MenuItem>
              <MenuItem value="spam">{t("deck_viewer_report_spam")}</MenuItem>
              <MenuItem value="low-quality">{t("deck_viewer_report_low_quality")}</MenuItem>
            </MuiSelect>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t("deck_viewer_report_details")}
            value={reportMessage}
            onChange={(e) => setReportMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>{t("cancel")}</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={async () => {
              try {
                await Deck.reportDeck(props.previewDeckId, reportReason, reportMessage || undefined);
                setReportSnackbar("Report submitted. Thank you!");
              } catch {
                setReportSnackbar("Failed to submit report.");
              }
              setReportDialogOpen(false);
              setReportMessage("");
            }}
          >
            {t("deck_viewer_submit_report")}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={reportSnackbar !== ""}
        autoHideDuration={4000}
        onClose={() => setReportSnackbar("")}
        message={reportSnackbar}
      />
    </>
  );
}
